import { createFileRoute, Outlet, Link, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft, Replace, Trash2, BookOpen, ChevronDown, Pencil, Loader2, CalendarDays,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { adminApi } from '@/lib/api/admin';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChangeCurriculumDialog } from '@/features/AdminBatches/ChangeCurriculumDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const Route = createFileRoute('/admin/batches/$batchId')({
  beforeLoad: () => {
    const { isAuthenticated, hasRole } = useAuthStore.getState();
    if (!isAuthenticated || !hasRole('super_admin')) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: AdminBatchLayout,
});

const TABS = [
  { label: 'Enrollment', to: '/admin/batches/$batchId/enrollment' },
  { label: 'Curriculum', to: '/admin/batches/$batchId/curriculum' },
  { label: 'Hackathons', to: '/admin/batches/$batchId/hackathons' },
] as const;

function AdminBatchLayout() {
  const { batchId } = Route.useParams();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location.pathname });
  const onCurriculumTab = location.endsWith('/curriculum');

  const [batchName, setBatchName] = useState('');
  const [meta, setMeta] = useState<{ students: number; topics: number; startDate: string; endDate: string } | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [changeCurriculumOpen, setChangeCurriculumOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminApi.getBatchStudents(batchId),
      adminApi.getBatch(batchId),
    ]).then(([enrollment, batch]) => {
      if (cancelled) return;
      setBatchName(enrollment.batch_name);
      setMeta({
        students: enrollment.students.length,
        topics: enrollment.total_topics,
        startDate: batch.start_date ?? '',
        endDate: batch.end_date ?? '',
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [batchId]);

  // Leaving the Curriculum tab hides the menu that owns these dialogs.
  useEffect(() => {
    if (!onCurriculumTab) {
      setChangeCurriculumOpen(false);
      setRemoveConfirmOpen(false);
    }
  }, [onCurriculumTab]);

  async function handleSaveName() {
    if (savingName) return;
    const trimmed = nameInput.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      setEditingName(false);
      return;
    }
    if (trimmed === batchName) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const updated = await adminApi.updateBatch(batchId, { name: trimmed });
      setBatchName(updated.name);
      toast.success('Batch renamed');
    } catch {
      toast.error('Failed to rename batch');
    } finally {
      setSavingName(false);
      setEditingName(false);
    }
  }

  const handleReplaceCurriculum = async () => {
    setReplacing(true);
    try {
      await adminApi.deleteBatchCurriculum(batchId);
      toast.success('Curriculum removed — assign a new one to reassign');
      navigate({ to: '/admin/curriculum' as never });
    } catch {
      toast.error('Failed to remove curriculum');
    } finally {
      setReplacing(false);
      setRemoveConfirmOpen(false);
    }
  };

  return (
    <div>
      {/* Header bar */}
      <div className="flex h-12 items-center gap-2 border-b border-border -mx-6 px-6">
        <button
          type="button"
          onClick={() => navigate({ to: '/admin/batches' as never })}
          className="flex flex-shrink-0 items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Batches
        </button>
        {batchName && (
          <>
            <span className="flex-shrink-0 text-muted-foreground/40">/</span>
            <div className="flex min-w-0 items-center gap-1 group">
              {editingName ? (
                <>
                  <input
                    autoFocus
                    className="text-sm font-semibold text-foreground bg-transparent border-b border-primary outline-none w-48"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    onBlur={handleSaveName}
                    disabled={savingName}
                    maxLength={100}
                  />
                  {savingName && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground flex-shrink-0" />}
                </>
              ) : (
                <>
                  <span className="min-w-0 truncate text-sm font-semibold text-foreground">{batchName}</span>
                  <button
                    type="button"
                    onClick={() => { setNameInput(batchName); setEditingName(true); }}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                    aria-label="Rename batch"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
            {/* Curriculum actions — one menu anchored to the batch name, kept
                clearly separate from the tab strip on the right. */}
            {onCurriculumTab && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 flex-shrink-0 gap-1 px-2 text-xs font-normal text-muted-foreground hover:text-foreground"
                  >
                    <BookOpen className="h-3 w-3" />
                    Curriculum
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuItem
                    onClick={() => setChangeCurriculumOpen(true)}
                    className="flex-col items-start gap-0.5 py-2"
                  >
                    <span className="flex items-center">
                      <Replace className="mr-2 h-3.5 w-3.5" />
                      Change curriculum…
                    </span>
                    <span className="pl-[22px] text-[11px] text-muted-foreground">
                      Replace with another batch or template
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setRemoveConfirmOpen(true)}
                    className="flex-col items-start gap-0.5 py-2 text-destructive focus:text-destructive"
                  >
                    <span className="flex items-center">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Remove curriculum
                    </span>
                    <span className="pl-[22px] text-[11px] text-muted-foreground">
                      Leave this batch with no curriculum
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}

        <div className="ml-auto flex items-center gap-3">
          {meta && (
            <div className="flex items-center gap-1.5">
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {meta.students} students · {meta.topics} topics
                {meta.startDate && (
                  <> · {new Date(meta.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  {meta.endDate && ` – ${new Date(meta.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}</>
                )}
              </span>
              <button
                type="button"
                onClick={() => setDateDialogOpen(true)}
                className="flex-shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Edit batch dates"
              >
                <CalendarDays className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {TABS.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              params={{ batchId }}
              className="flex h-12 items-center border-b-2 border-transparent px-4 text-sm font-medium capitalize text-muted-foreground transition-colors hover:text-foreground"
              activeProps={{
                className: 'flex h-12 items-center border-b-2 border-primary px-4 text-sm font-medium capitalize text-primary transition-colors',
              }}
            >
              {tab.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="-mx-6 -mb-6">
        <Outlet />
      </div>

      <AlertDialog
        open={removeConfirmOpen}
        onOpenChange={(open) => { if (!replacing) setRemoveConfirmOpen(open); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this batch's curriculum?</AlertDialogTitle>
            <AlertDialogDescription>
              Every module, topic, session, resource and assessment in this batch's curriculum is
              permanently deleted, along with student progress and submissions on them. You'll be
              taken to the Curriculum page to assign a new one. This cannot be undone.
              <br />
              <br />
              To swap in a different curriculum instead — keeping students enrolled — use{' '}
              <span className="font-medium text-foreground">Change curriculum</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={replacing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleReplaceCurriculum(); }}
              disabled={replacing}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {replacing ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Removing…</>
              ) : (
                'Remove curriculum'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {changeCurriculumOpen && batchName && (
        <ChangeCurriculumDialog
          batchId={batchId}
          batchName={batchName}
          onClose={() => setChangeCurriculumOpen(false)}
          onSwapped={() => {
            // The curriculum editor below loads its modules in its own effect
            // and the header counts come from a separate fetch, so a reload is
            // the honest way to show the new curriculum everywhere at once.
            window.location.reload();
          }}
        />
      )}

      {meta && (
        <EditDatesDialog
          open={dateDialogOpen}
          onClose={() => setDateDialogOpen(false)}
          batchId={batchId}
          initialStart={meta.startDate}
          initialEnd={meta.endDate}
          onSaved={(s, e) => setMeta((prev) => prev ? { ...prev, startDate: s, endDate: e } : prev)}
        />
      )}
    </div>
  );
}

function EditDatesDialog({
  open,
  onClose,
  batchId,
  initialStart,
  initialEnd,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  batchId: string;
  initialStart: string;
  initialEnd: string;
  onSaved: (startDate: string, endDate: string) => void;
}) {
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStartDate(initialStart);
      setEndDate(initialEnd);
      setError('');
    }
  }, [open, initialStart, initialEnd]);

  async function handleSave() {
    if (startDate && endDate && endDate < startDate) {
      setError('End date must be after start date');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await adminApi.updateBatch(batchId, { start_date: startDate, end_date: endDate });
      toast.success('Dates updated');
      onSaved(startDate, endDate);
      onClose();
    } catch {
      setError('Failed to update dates. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-xs">
        <DialogHeader>
          <DialogTitle>Edit batch dates</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label htmlFor="edit-start-date" className="text-sm font-medium text-foreground">Start date</label>
            <input
              id="edit-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="edit-end-date" className="text-sm font-medium text-foreground">End date</label>
            <input
              id="edit-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
